import { request } from '../../api';

export type RoleName =
  | 'cashier'
  | 'operator'
  | 'site_manager'
  | 'marketer'
  | 'network_admin'
  | 'auditor';

export type EmployeeStatus = 'active' | 'suspended' | 'dismissed';

export interface EmployeeRole {
  readonly role: RoleName;
  readonly siteId: string | null;
}

export interface Employee {
  readonly id: string;
  readonly displayName: string;
  readonly phone: string | null;
  readonly status: EmployeeStatus;
  readonly hasPin: boolean;
  readonly dismissedAt: string | null;
  readonly createdAt: string;
  readonly roles: readonly EmployeeRole[];
  readonly login: string | null;
  readonly userId: string | null;
  readonly userStatus: 'active' | 'suspended' | null;
  readonly mustChangePassword: boolean;
  readonly passwordChangedAt: string | null;
  readonly lockedUntil: string | null;
  readonly totpConfirmed: boolean;
}

export function listEmployees(filters: {
  siteId?: string | null;
  status?: string;
  dismissed?: boolean;
}): Promise<readonly Employee[]> {
  const p = new URLSearchParams();
  if (filters.siteId) p.set('site_id', filters.siteId);
  if (filters.status) p.set('status', filters.status);
  if (filters.dismissed) p.set('dismissed', '1');
  const qs = p.toString();
  return request('GET', `/api/v1/employees${qs ? `?${qs}` : ''}`);
}

export function getEmployee(id: string): Promise<Employee> {
  return request('GET', `/api/v1/employees/${encodeURIComponent(id)}`);
}

/**
 * Какие роли актор вправе выдать и на каких объектах.
 *
 * Считает сервер. Копия правила в интерфейсе разошлась бы с матрицей прав при
 * первой же правке — ровно как разошлась бы копия состава меню.
 */
export interface GrantableRoles {
  readonly roles: readonly RoleName[];
  readonly network: boolean;
  readonly sites: readonly string[];
}

export function grantableRoles(): Promise<GrantableRoles> {
  return request('GET', '/api/v1/employees/meta/roles');
}

export interface CreateEmployeeInput {
  readonly displayName: string;
  readonly phone?: string | null;
  readonly login: string;
  readonly password: string;
  readonly pin?: string;
  readonly roles: readonly EmployeeRole[];
}

export function createEmployee(input: CreateEmployeeInput): Promise<Employee> {
  return request('POST', '/api/v1/employees', input);
}

export function updateEmployee(
  id: string,
  input: { displayName?: string; phone?: string | null },
): Promise<Employee> {
  return request('PATCH', `/api/v1/employees/${encodeURIComponent(id)}`, input);
}

export function grantRole(id: string, role: EmployeeRole): Promise<Employee> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/roles`, role);
}

export function revokeRole(id: string, role: EmployeeRole): Promise<Employee> {
  return request('DELETE', `/api/v1/employees/${encodeURIComponent(id)}/roles`, role);
}

export function setStatus(id: string, status: EmployeeStatus): Promise<Employee> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/status`, { status });
}

export function setPassword(id: string, password: string): Promise<{ ok: boolean }> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/password`, { password });
}

export function setPin(id: string, pin: string): Promise<{ ok: boolean }> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/pin`, { pin });
}

export function resetTotp(id: string): Promise<{ ok: boolean }> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/totp/reset`);
}

export function revokeSessions(id: string): Promise<{ revoked: number }> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/sessions/revoke`);
}

export function unlock(id: string): Promise<{ ok: boolean }> {
  return request('POST', `/api/v1/employees/${encodeURIComponent(id)}/unlock`);
}

/* ------------------------------------------------------- собственный профиль */

export function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean }> {
  return request('POST', '/api/v1/auth/password', { currentPassword, newPassword });
}

export interface TotpEnrollment {
  readonly secret: string;
  readonly uri: string;
}

export function enrollOwnTotp(): Promise<TotpEnrollment> {
  return request('POST', '/api/v1/auth/totp/enroll');
}

export function confirmOwnTotp(code: string): Promise<{ verified: boolean }> {
  return request('POST', '/api/v1/auth/totp/confirm', { code });
}
